/*
 * Copyright (c) 2025 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *  Ipivot Corporation ("Confidential Information").
 */
import { Col, Card } from 'react-bootstrap';
import { useTheme } from 'styled-components';
import { Link } from 'react-router-dom';
import { useAnimatedNumber } from './useAnimatedNumber';
import { Supercat } from '../data';

interface SupercatData extends Supercat {
    categories: number;
    products: number;
    applications: number;
}

interface SupercatCardProps {
    supercat: SupercatData;
    animationDelay?: number;
    animate?: boolean;
    isPlaceholder?: boolean;
}

export function SupercatCard({ supercat, animationDelay = 0, animate = true, isPlaceholder = false }: SupercatCardProps) {
    const theme: any = useTheme();

    // Card animation duration in ms
    const cardAnimDuration = 200;

    // Numbers start animating after card animation completes
    const numberAnimDelay = animate ? animationDelay + cardAnimDuration : 0;

    const animatedCategories = useAnimatedNumber(supercat.categories, { delay: numberAnimDelay, enabled: animate && !isPlaceholder });
    const animatedProducts = useAnimatedNumber(supercat.products, { delay: numberAnimDelay, enabled: animate && !isPlaceholder });
    const animatedApplications = useAnimatedNumber(supercat.applications, { delay: numberAnimDelay, enabled: animate && !isPlaceholder });

    const cardStyle = {
        backgroundColor: theme.colors.dark,
        borderColor: theme.colors.grey,
        color: theme.colors.text,
        animation: animate ? `dropCard ${cardAnimDuration}ms ease-out ${animationDelay}ms both` : undefined,
        opacity: isPlaceholder ? 0 : undefined,
        visibility: isPlaceholder ? ('hidden' as const) : undefined,
        cursor: isPlaceholder ? undefined : 'pointer',
        textDecoration: 'none',
    };
    const headerStyle = {
        backgroundColor: theme.colors.headerBg,
        borderColor: theme.colors.grey,
        color: theme.colors.text,
        padding: '0.75rem 1rem',
        margin: 0,
        minHeight: '4rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    };
    const textStyle = { color: theme.colors.textMuted };
    const cardBodyStyle = {
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'space-between',
        height: '100%',
    };

    return (
        <>
            <style>{`
                @keyframes dropCard {
                    0% {
                        opacity: 0;
                        transform: translateY(-20px) scale(1.35);
                    }
                    60% {
                        opacity: 1;
                        transform: translateY(0) scale(1.05);
                    }
                    100% {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .supercat-card-link .card {
                    transition: transform 0.2s ease, box-shadow 0.2s ease;
                }
                .supercat-card-link:hover .card {
                    transform: translateY(-4px);
                    box-shadow: 0 4px 12px rgba(0, 255, 255, 0.2);
                }
            `}</style>
            <Col key={supercat.key} xs={12} sm={6} lg={3}>
                <Link to={supercat.key === 'ALL' ? '/categories' : `/supercat/${supercat.key}`} style={{ textDecoration: 'none', color: 'inherit' }} className="supercat-card-link">
                    <Card className="h-100" style={cardStyle}>
                        <Card.Header as="h5" style={headerStyle}>{supercat.title}</Card.Header>
                        <Card.Body style={cardBodyStyle}>
                            <div style={{ marginTop: '-10px', textAlign: 'right', alignSelf: 'flex-end' }}>
                                Categories: <span style={{ fontWeight: 'bold', fontSize: '1.5rem', color: theme.colors.info }}>{animatedCategories}</span>
                                <div style={{ fontSize: '0.85rem', color: theme.colors.textMuted }}>
                                    {animatedApplications} Applications
                                </div>
                            </div>
                            <div>
                                <Card.Text style={{ ...textStyle, textAlign: 'center', marginBottom: '0.25rem' }}>
                                    {supercat.description}
                                </Card.Text>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                <div style={{ width: '50%', textAlign: 'right', paddingRight: '0.5rem' }}>
                                    <div className="digital-number" style={{ fontSize: '2rem', fontWeight: 'bold', color: theme.colors.ctext }}>{animatedProducts}</div>
                                </div>
                                <div style={{ width: '50%', textAlign: 'left', paddingLeft: '0.5rem' }}>
                                    <div style={{ fontSize: '1rem' }}>Risk Routes</div>
                                </div>
                            </div>
                        </Card.Body>
                    </Card>
                </Link>
            </Col>
        </>
    );
}
